/* @flow */

import _ from 'lodash'
import { BackgroundScheduler } from './'
import type { AnalysisResult, CodeReference } from './Worker/BackgroundScheduler'

/**
 * CodeEditor typename
 * @private
 * @memberof CodeEditorService
 */

/**
 * Finds the elements in given elements array by name, and return their ids
 * @function
 * @memberof CodeEditorService
 *
 * @return Array<string> The array containing the ids of found elements
 */
const getIdsForMatchedElementsNames = (
  matches: Array<string>,
  elements: Array<any>
): Array<string> => {
  return matches.reduce((acc, cur) => {
    const elem = elements.find(e => e.name === cur)
    if (elem && acc.indexOf(elem.id) === -1) return [...acc, elem.id]
    return acc
  }, [])
}

const getNoneExistingFlowsFromMatches = (
  flowNameMatches: Array<CodeReference>,
  appFlows: Array<any>
): Array<CodeReference> => {
  return _.differenceWith(
    flowNameMatches,
    appFlows,
    (m: CodeReference, flow) =>
      m.matchedRef === flow.name ||
      (flow.typeConfig && m.matchedRef === flow.typeConfig.subjectPrefix)
  )
}

const positionalAnchors = ['<above>', '<below>', '<left-of>', '<right-of>', '<nearest>']

/**
 * Filters given field matchers so that only positional field matchers are returned
 * @function
 * @memberof CodeEditorService
 *
 * @return Array<Object> Array of positional field matchers
 */
const getCodeFieldsUsingPositionalAnchors = (
  appFields: Array<any>,
  fieldNameMatches: Array<CodeReference>
): Array<CodeReference> => {
  return fieldNameMatches.filter((fieldNameMatcher: CodeReference) => {
    const field = _.find(appFields, { name: fieldNameMatcher.matchedRef })
    return field && positionalAnchors.some(anchor => field.path.includes(anchor))
  })
}

type ModuleReferenceAnalysisResult = {
  ref: CodeReference,
  fieldRefs: Array<CodeReference>,
  children: Array<ModuleReferenceAnalysisResult>
}

/**
 * Produces a tree of code analysis results matching the tree of module includes
 * spanning from the given flow references (names). Invalid flow references are ignored
 * @return Array of analysis tree roots
 */
const analyzeIncludedModuleFlowsRecursively = (
  flowRefs: Array<CodeReference>,
  allFlows: Array<Object>
): Promise<Array<ModuleReferenceAnalysisResult>> => {
  return Promise.all(
    _.compact(
      _.uniqBy(flowRefs, (fr: CodeReference) => fr.matchedRef).map((flowRef: CodeReference) => {
        const flowOfRef = _.find(
          allFlows,
          flow =>
            flow.type === 'MODULE' &&
            (flow.name === flowRef.matchedRef ||
              (flow.typeConfig && flow.typeConfig.subjectPrefix === flowRef.matchedRef))
        )
        if (!flowOfRef) return null
        return (
          // The .code property should always contain es5 code (until manatee understands newer standards)
          codeAnalyzerFn(flowOfRef.code, false)
            .then(flowAnalysis =>
              analyzeIncludedModuleFlowsRecursively(flowAnalysis.flowRefs, allFlows).then(
                childAnalyses => ({
                  ref: flowRef,
                  fieldRefs: flowAnalysis.fieldRefs || [],
                  children: childAnalyses || []
                })
              )
            )
            .catch(e => {
              console.warn('Failed to analyze flow', flowOfRef.name, e)
              return { ref: flowRef, fieldRef: [], children: [] }
            })
        )
      })
    )
  )
}

// Let this be mockable
let codeAnalyzerFn: (string, boolean) => Promise<AnalysisResult> = (code, isModern) =>
  Promise.resolve({ vars: [], flowRefs: [], fieldRefs: [], semanticComments: [], apiUsage: {} })
setTimeout(() => (codeAnalyzerFn = BackgroundScheduler.defaultScheduler.scheduleCodeAnalysis))

export type ModuleIncludeReport = {
  ref: CodeReference,
  reportLines: Array<string>
}

/**
 * Finds bad field references in modules included from the given flow references
 * @return Array<string> Array of report lines on the form
 * moduleRef1: (badRef1, badRef2)
 * moduleRef1 -> child1: (badRef3)
 * moduleRef1 -> child2: (badRef4)
 * moduleRef1 -> child3 (badRef5) -> grandchild: (badRef6)
 */
const getModuleIncludesWithBadFieldRefs = (
  flowRefs: Array<CodeReference>,
  allFlows: Array<any>,
  appFields: Array<any>
): Promise<Array<ModuleIncludeReport>> => {
  return analyzeIncludedModuleFlowsRecursively(flowRefs, allFlows).then(flowAnalyses => {
    const getChildReports = (child: ModuleReferenceAnalysisResult): string[] => {
      const badRefsInChild = _.uniq((child.fieldRefs || []).map(fr => fr.matchedRef)).filter(
        matchedRef => _.every(appFields, af => af.name !== matchedRef)
      )
      const badRefsInGrandchildren: string[] = _.compact(child.children.map(getChildReports))
      const childReportContribution = badRefsInChild.length
        ? `${child.ref.matchedRef.toString()}: (${badRefsInChild.join(', ')})`
        : ''
      const grandchildReportLines = _.flatMap(badRefsInGrandchildren, br =>
        br.map(l => `${child.ref.matchedRef.toString()} -> ${l}`)
      )
      return _.compact([childReportContribution, ...grandchildReportLines])
    }
    return _.map(flowAnalyses, flowAnalysis => {
      const flowReports = getChildReports(flowAnalysis)
      return {
        ref: flowAnalysis.ref,
        reportLines: flowReports
      }
    })
  })
}
const self = {
  forTest: {
    mockCodeAnalyzerFn(analyzerMock: (string, boolean) => Promise<AnalysisResult>) {
      codeAnalyzerFn = analyzerMock
    }
  },
  getIdsForMatchedElementsNames,
  getNoneExistingFlowsFromMatches,
  getCodeFieldsUsingPositionalAnchors,
  positionalAnchors,
  getModuleIncludesWithBadFieldRefs
}

/**
 *
 * @module CodeEditorService
 * @namespace CodeEditorService
 * @version 1.0.0
 */
export default self
